import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ChevronLeft, Search, X, Download, Upload,
  CheckCircle2, AlertTriangle, AlertCircle,
  ChevronDown, ChevronUp, Shield, ExternalLink,
} from 'lucide-react';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { CustomDropdown, type DropdownOption } from '../../components/ui/CustomDropdown';

// ── Types ──────────────────────────────────────────────────────────────────

interface StateTaxRule {
  id: string;
  stateCode: string;
  stateName: string;
  laborTaxable: boolean;
  materialsTaxable: boolean;
  packingServicesTaxable: boolean;
  storageTaxable: boolean;
  sourceUrl: string;
  lastVerified: string;
  notes: string;
  updatedAt: string;
  updatedBy: string;
}

interface InterstateTaxRules {
  governingStateRule: 'origin' | 'destination' | 'manual_review';
  quoteDisclaimerText: string;
  interstateDefinition: string;
  updatedAt: string;
  updatedBy: string;
}

interface TaxAuditEntry {
  id: string;
  entityType: 'state_rule' | 'interstate_rules';
  entityId: string;
  field: string;
  oldValue: string;
  newValue: string;
  changedBy: string;
  changedByName: string;
  changedAt: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function monthsAgo(dateStr: string): number {
  const d = new Date(dateStr);
  const now = new Date();
  return (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
}

function verificationStatus(dateStr: string): 'current' | 'warning' | 'overdue' {
  const months = monthsAgo(dateStr);
  if (months > 18) return 'overdue';
  if (months >= 12) return 'warning';
  return 'current';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function timeAgo(dateStr: string): string {
  const months = monthsAgo(dateStr);
  if (months < 1) return 'This month';
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (rem === 0) return `${years} year${years > 1 ? 's' : ''} ago`;
  return `${years}y ${rem}mo ago`;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++; // skip escaped quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const vals = parseCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj;
  });
}

// ── Filter types ───────────────────────────────────────────────────────────

type LaborFilter = 'all' | 'yes' | 'no';
type MaterialsFilter = 'all' | 'yes' | 'no';
type VerificationFilter = 'all' | 'current' | 'overdue';

// ── Status badge ───────────────────────────────────────────────────────────

function VerificationBadge({ status }: { status: 'current' | 'warning' | 'overdue' }) {
  if (status === 'current') return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
      <CheckCircle2 size={12} /> Current
    </span>
  );
  if (status === 'warning') return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
      <AlertTriangle size={12} /> Aging
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
      <AlertCircle size={12} /> Overdue
    </span>
  );
}

function TaxableBadge({ taxable }: { taxable: boolean }) {
  return taxable
    ? <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Yes</span>
    : <span className="text-[11px] font-medium text-slate bg-stone/50 px-2 py-0.5 rounded-full">No</span>;
}

// ── Edit Modal ─────────────────────────────────────────────────────────────

function EditStateModal({
  rule,
  onSave,
  onClose,
}: {
  rule: StateTaxRule;
  onSave: (updates: Partial<StateTaxRule>) => Promise<void>;
  onClose: () => void;
}) {
  const [laborTaxable, setLaborTaxable] = useState(rule.laborTaxable);
  const [materialsTaxable, setMaterialsTaxable] = useState(rule.materialsTaxable);
  const [packingServicesTaxable, setPackingServicesTaxable] = useState(rule.packingServicesTaxable ?? false);
  const [storageTaxable, setStorageTaxable] = useState(rule.storageTaxable ?? false);
  const [sourceUrl, setSourceUrl] = useState(rule.sourceUrl ?? '');
  const [lastVerified, setLastVerified] = useState(rule.lastVerified);
  const [notes, setNotes] = useState(rule.notes);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const taxableChanged = laborTaxable !== rule.laborTaxable || materialsTaxable !== rule.materialsTaxable
    || packingServicesTaxable !== (rule.packingServicesTaxable ?? false) || storageTaxable !== (rule.storageTaxable ?? false);

  const handleSubmit = () => {
    if (taxableChanged) {
      setShowConfirm(true);
    } else {
      doSave();
    }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await onSave({ laborTaxable, materialsTaxable, packingServicesTaxable, storageTaxable, sourceUrl, lastVerified, notes });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-midnight/70" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight font-semibold">
            Edit {rule.stateName} ({rule.stateCode})
          </h2>
          <button type="button" onClick={onClose} className="p-1 text-slate hover:text-midnight">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Labor Taxable */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Labor Taxable</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLaborTaxable(true)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${laborTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >Yes</button>
              <button
                type="button"
                onClick={() => setLaborTaxable(false)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${!laborTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >No</button>
            </div>
          </div>

          {/* Materials Taxable */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Materials Taxable</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMaterialsTaxable(true)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${materialsTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >Yes</button>
              <button
                type="button"
                onClick={() => setMaterialsTaxable(false)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${!materialsTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >No</button>
            </div>
          </div>

          {/* Packing Services Taxable */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Packing Services Taxable</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPackingServicesTaxable(true)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${packingServicesTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >Yes</button>
              <button
                type="button"
                onClick={() => setPackingServicesTaxable(false)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${!packingServicesTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >No</button>
            </div>
          </div>

          {/* Storage Taxable */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Storage Taxable</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStorageTaxable(true)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${storageTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >Yes</button>
              <button
                type="button"
                onClick={() => setStorageTaxable(false)}
                className={`flex-1 h-11 rounded-lg border text-small font-medium transition-colors ${!storageTaxable ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-slate hover:border-plum/40'}`}
              >No</button>
            </div>
          </div>

          {/* Source URL */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Source URL</label>
            <input
              type="url"
              value={sourceUrl}
              onChange={e => setSourceUrl(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-stone text-small text-midnight
                         focus:border-plum focus:ring-2 focus:ring-plum/20 outline-none"
              placeholder="https://example.gov/tax-regulations..."
            />
          </div>

          {/* Last Verified */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Last Verified</label>
            <input
              type="date"
              value={lastVerified}
              onChange={e => setLastVerified(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-stone text-small text-midnight
                         focus:border-plum focus:ring-2 focus:ring-plum/20 outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-[13px] font-medium text-midnight mb-1 block">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-stone text-small text-midnight
                         focus:border-plum focus:ring-2 focus:ring-plum/20 outline-none resize-none"
              placeholder="Optional notes about this state's tax rules..."
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 btn-secondary">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={saving} className="flex-1 btn-primary">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {showConfirm && (
          <ConfirmModal
            isOpen
            title="Change Taxability?"
            message="Changing taxability settings will affect tax display on all future quotes in this state. Are you sure you want to proceed?"
            confirmLabel="Yes, Change"
            variant="primary"
            onConfirm={() => { setShowConfirm(false); doSave(); }}
            onClose={() => setShowConfirm(false)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── State Card ─────────────────────────────────────────────────────────────

function StateCard({
  rule,
  onEdit,
  onVerify,
}: {
  rule: StateTaxRule;
  onEdit: () => void;
  onVerify: () => void;
}) {
  const status = verificationStatus(rule.lastVerified);

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 shadow-brand-sm">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-midnight">{rule.stateName}</span>
          <span className="text-[11px] text-slate font-medium bg-stone/30 px-1.5 py-0.5 rounded">{rule.stateCode}</span>
        </div>
        <VerificationBadge status={status} />
      </div>

      {/* Fields grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
        <div>
          <span className="text-[11px] text-slate block">Labor Taxable</span>
          <TaxableBadge taxable={rule.laborTaxable} />
        </div>
        <div>
          <span className="text-[11px] text-slate block">Materials Taxable</span>
          <TaxableBadge taxable={rule.materialsTaxable} />
        </div>
        <div>
          <span className="text-[11px] text-slate block">Packing Services</span>
          <TaxableBadge taxable={rule.packingServicesTaxable ?? false} />
        </div>
        <div>
          <span className="text-[11px] text-slate block">Storage</span>
          <TaxableBadge taxable={rule.storageTaxable ?? false} />
        </div>
        <div className="col-span-2">
          <span className="text-[11px] text-slate block">Last Verified</span>
          <span className="text-[13px] text-midnight">{formatDate(rule.lastVerified)}</span>
          <span className="text-[11px] text-slate ml-1">({timeAgo(rule.lastVerified)})</span>
        </div>
        {rule.notes && (
          <div className="col-span-2">
            <span className="text-[11px] text-slate block">Notes</span>
            <span className="text-[13px] text-midnight line-clamp-2">{rule.notes}</span>
          </div>
        )}
        {rule.sourceUrl && (
          <div className="col-span-2">
            <a
              href={rule.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-plum hover:text-plum/80 transition-colors"
            >
              <ExternalLink size={12} />
              Source
            </a>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button type="button" onClick={onEdit} className="flex-1 btn-secondary text-[13px] h-9">
          Edit
        </button>
        <button type="button" onClick={onVerify} className="flex-1 btn-ghost text-[13px] h-9">
          <Shield size={14} className="mr-1" /> Mark Verified
        </button>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export function TaxSettingsPage() {
  const navigate = useNavigate();

  // State tax rules
  const [rules, setRules] = useState<StateTaxRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Interstate rules
  const [interstate, setInterstate] = useState<InterstateTaxRules | null>(null);
  const [savingInterstate, setSavingInterstate] = useState(false);

  // Audit log
  const [auditLog, setAuditLog] = useState<TaxAuditEntry[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [laborFilter, setLaborFilter] = useState<LaborFilter>('all');
  const [materialsFilter, setMaterialsFilter] = useState<MaterialsFilter>('all');
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('all');

  // Edit modal
  const [editingRule, setEditingRule] = useState<StateTaxRule | null>(null);

  // Upload refs
  const stateUploadRef = useRef<HTMLInputElement>(null);
  const interstateUploadRef = useRef<HTMLInputElement>(null);

  // ── Fetch data ───────────────────────────────────────────────────────────

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: StateTaxRule[] }>('/admin/tax-settings/states');
      if (res.success) setRules(res.data);
    } catch (err) {
      setError('Failed to load state tax rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterstate = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: InterstateTaxRules }>('/admin/tax-settings/interstate');
      if (res.success) setInterstate(res.data);
    } catch (err) {
      console.error('Failed to load interstate rules:', err);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: TaxAuditEntry[] }>('/admin/tax-settings/audit-log?limit=200');
      if (res.success) setAuditLog(res.data);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); fetchInterstate(); }, [fetchRules, fetchInterstate]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSaveState = async (stateCode: string, updates: Partial<StateTaxRule>) => {
    const res = await api.patch<{ success: boolean; data: StateTaxRule }>(`/admin/tax-settings/states/${stateCode}`, updates);
    if (res.success) {
      setRules(prev => prev.map(r => r.stateCode === stateCode ? res.data : r));
    }
  };

  const handleVerify = async (stateCode: string) => {
    const res = await api.post<{ success: boolean; data: StateTaxRule }>(`/admin/tax-settings/states/${stateCode}/verify`);
    if (res.success) {
      setRules(prev => prev.map(r => r.stateCode === stateCode ? res.data : r));
    }
  };

  const handleSaveInterstate = async (updates: Partial<InterstateTaxRules>) => {
    setSavingInterstate(true);
    try {
      const res = await api.patch<{ success: boolean; data: InterstateTaxRules }>('/admin/tax-settings/interstate', updates);
      if (res.success) setInterstate(res.data);
    } catch (err) {
      console.error('Failed to update interstate rules:', err);
    } finally {
      setSavingInterstate(false);
    }
  };

  // ── CSV Download/Upload — States ─────────────────────────────────────────

  const downloadStatesCsv = () => {
    const header = 'stateCode,stateName,laborTaxable,materialsTaxable,packingServicesTaxable,storageTaxable,sourceUrl,lastVerified,notes\n';
    const rows_csv = rules.map(r =>
      `${r.stateCode},${r.stateName},${r.laborTaxable},${r.materialsTaxable},${r.packingServicesTaxable ?? false},${r.storageTaxable ?? false},"${(r.sourceUrl || '').replace(/"/g, '""')}",${r.lastVerified},"${(r.notes || '').replace(/"/g, '""')}"`
    ).join('\n');
    const blob = new Blob([header + rows_csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'state-tax-settings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStatesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const parsed = parseCsv(text);
      const res = await api.post<{ success: boolean; data: { matched: number; created: number; total: number } }>('/admin/tax-settings/states/bulk-upload', { rows: parsed });
      if (res.success) {
        await fetchRules();
        const parts: string[] = [];
        if (res.data.matched > 0) parts.push(`${res.data.matched} updated`);
        if (res.data.created > 0) parts.push(`${res.data.created} created`);
        const skipped = res.data.total - res.data.matched - res.data.created;
        if (skipped > 0) parts.push(`${skipped} skipped`);
        alert(`${parts.join(', ')} (${res.data.total} rows total).`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Check the CSV format.');
    } finally {
      e.target.value = '';
    }
  };

  // ── CSV Download/Upload — Interstate ─────────────────────────────────────

  const downloadInterstateCsv = () => {
    if (!interstate) return;
    const header = 'governingStateRule,quoteDisclaimerText,interstateDefinition\n';
    const row_csv = `${interstate.governingStateRule},"${(interstate.quoteDisclaimerText || '').replace(/"/g, '""')}",${interstate.interstateDefinition}`;
    const blob = new Blob([header + row_csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interstate-tax-settings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleInterstateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const parsed = parseCsv(text);
      if (parsed.length === 0) { alert('No data found in CSV.'); return; }
      const row = parsed[0];
      const res = await api.post<{ success: boolean; data: InterstateTaxRules }>('/admin/tax-settings/interstate/bulk-upload', row);
      if (res.success) {
        setInterstate(res.data);
        alert('Interstate rules updated from CSV.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Check the CSV format.');
    } finally {
      e.target.value = '';
    }
  };

  // ── Filtering ────────────────────────────────────────────────────────────

  const filtered = rules.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      if (!r.stateName.toLowerCase().includes(q) && !r.stateCode.toLowerCase().includes(q)) return false;
    }
    if (laborFilter === 'yes' && !r.laborTaxable) return false;
    if (laborFilter === 'no' && r.laborTaxable) return false;
    if (materialsFilter === 'yes' && !r.materialsTaxable) return false;
    if (materialsFilter === 'no' && r.materialsTaxable) return false;
    if (verificationFilter === 'overdue' && verificationStatus(r.lastVerified) !== 'overdue') return false;
    if (verificationFilter === 'current' && verificationStatus(r.lastVerified) === 'overdue') return false;
    return true;
  });

  const overdueCount = rules.filter(r => verificationStatus(r.lastVerified) === 'overdue').length;

  // ── Dropdown options ─────────────────────────────────────────────────────

  const laborOptions: DropdownOption[] = [
    { value: 'all', label: 'All' },
    { value: 'yes', label: 'Taxable' },
    { value: 'no', label: 'Not Taxable' },
  ];
  const materialsOptions: DropdownOption[] = [
    { value: 'all', label: 'All' },
    { value: 'yes', label: 'Taxable' },
    { value: 'no', label: 'Not Taxable' },
  ];
  const verificationOptions: DropdownOption[] = [
    { value: 'all', label: 'All' },
    { value: 'current', label: 'Current' },
    { value: 'overdue', label: 'Overdue' },
  ];
  const governingOptions: DropdownOption[] = [
    { value: 'origin', label: 'Origin State' },
    { value: 'destination', label: 'Destination State' },
    { value: 'manual_review', label: 'Flag for Manual Review' },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools/billing')}
        className="flex items-center gap-1 text-small text-slate hover:text-plum mb-4 -ml-1 transition-colors"
      >
        <ChevronLeft size={16} />
        Billing & Pricing
      </button>

      <h1 className="font-display text-h1 text-[var(--color-text-primary)] mb-1">Tax Settings</h1>
      <p className="text-small text-[var(--color-text-secondary)] mb-6">
        Configure state-by-state taxability rules for moving service quotes and invoices.
      </p>

      {/* ── Overdue Banner ────────────────────────────────────────────────── */}
      {overdueCount > 0 && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <AlertCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-semibold text-red-800">
              {overdueCount} state{overdueCount > 1 ? 's have' : ' has'} tax rules that haven't been verified in over 18 months.
            </p>
            <p className="text-[13px] text-red-700 mt-0.5">Review recommended.</p>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ── STATE TAX RULES TABLE ──────────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display text-h3 text-midnight font-semibold">State Tax Rules</h2>
          <div className="flex gap-2">
            <button type="button" onClick={downloadStatesCsv} className="btn-ghost text-[13px] h-9 px-3">
              <Download size={14} className="mr-1" /> Download
            </button>
            <button type="button" onClick={() => stateUploadRef.current?.click()} className="btn-ghost text-[13px] h-9 px-3">
              <Upload size={14} className="mr-1" /> Upload
            </button>
            <input ref={stateUploadRef} type="file" accept=".csv" onChange={handleStatesUpload} className="hidden" />
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col gap-3 mb-4">
          {/* Search bar */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search states..."
              className="w-full h-11 pl-9 pr-9 rounded-lg border border-stone text-small text-midnight
                         focus:border-plum focus:ring-2 focus:ring-plum/20 outline-none"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight">
                <X size={16} />
              </button>
            )}
          </div>

          {/* Filter row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] text-slate block mb-1">Labor</label>
              <CustomDropdown
                options={laborOptions}
                value={laborFilter}
                onChange={v => setLaborFilter(v as LaborFilter)}
              />
            </div>
            <div>
              <label className="text-[11px] text-slate block mb-1">Materials</label>
              <CustomDropdown
                options={materialsOptions}
                value={materialsFilter}
                onChange={v => setMaterialsFilter(v as MaterialsFilter)}
              />
            </div>
            <div>
              <label className="text-[11px] text-slate block mb-1">Verification</label>
              <CustomDropdown
                options={verificationOptions}
                value={verificationFilter}
                onChange={v => setVerificationFilter(v as VerificationFilter)}
              />
            </div>
          </div>
        </div>

        {/* Results count */}
        <p className="text-[11px] text-slate mb-3">
          Showing {filtered.length} of {rules.length} states
        </p>

        {/* State Cards */}
        <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4">
          {filtered.map(rule => (
            <StateCard
              key={rule.stateCode}
              rule={rule}
              onEdit={() => setEditingRule(rule)}
              onVerify={() => handleVerify(rule.stateCode)}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate text-small">
            No states match your filters.
          </div>
        )}
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ── INTERSTATE RULES ───────────────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display text-h3 text-midnight font-semibold">Interstate Rules</h2>
          <div className="flex gap-2">
            <button type="button" onClick={downloadInterstateCsv} className="btn-ghost text-[13px] h-9 px-3">
              <Download size={14} className="mr-1" /> Download
            </button>
            <button type="button" onClick={() => interstateUploadRef.current?.click()} className="btn-ghost text-[13px] h-9 px-3">
              <Upload size={14} className="mr-1" /> Upload
            </button>
            <input ref={interstateUploadRef} type="file" accept=".csv" onChange={handleInterstateUpload} className="hidden" />
          </div>
        </div>

        {interstate && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 md:p-6 shadow-brand-sm flex flex-col gap-5">
            {/* Governing State Rule */}
            <div>
              <label className="text-[13px] font-medium text-midnight mb-1 block">
                Governing State Rule
              </label>
              <p className="text-[11px] text-slate mb-2">
                Which state's tax rules apply when origin and destination differ?
              </p>
              <CustomDropdown
                options={governingOptions}
                value={interstate.governingStateRule}
                onChange={v => handleSaveInterstate({ governingStateRule: v as InterstateTaxRules['governingStateRule'] })}
              />
            </div>

            {/* Quote Disclaimer Text */}
            <div>
              <label className="text-[13px] font-medium text-midnight mb-1 block">
                Quote Disclaimer Text
              </label>
              <p className="text-[11px] text-slate mb-2">
                Disclaimer shown on interstate quotes in the client portal.
              </p>
              <textarea
                value={interstate.quoteDisclaimerText}
                onChange={e => setInterstate(prev => prev ? { ...prev, quoteDisclaimerText: e.target.value } : prev)}
                onBlur={e => handleSaveInterstate({ quoteDisclaimerText: e.target.value })}
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg border border-stone text-small text-midnight
                           focus:border-plum focus:ring-2 focus:ring-plum/20 outline-none resize-none"
                placeholder="e.g. Tax calculations for interstate moves are estimates..."
              />
            </div>

            {/* Interstate definition */}
            <div>
              <label className="text-[13px] font-medium text-midnight mb-1 block">
                Interstate Move Definition
              </label>
              <p className="text-[11px] text-slate">
                A move is classified as interstate when the origin state differs from the destination state.
              </p>
            </div>

            {savingInterstate && (
              <p className="text-[11px] text-plum">Saving…</p>
            )}
          </div>
        )}
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ── AUDIT LOG ──────────────────────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <section className="mb-10">
        <button
          type="button"
          onClick={() => {
            const nextOpen = !auditOpen;
            setAuditOpen(nextOpen);
            if (nextOpen && auditLog.length === 0) fetchAuditLog();
          }}
          className="flex items-center gap-2 w-full text-left py-3 text-[15px] font-semibold text-midnight hover:text-plum transition-colors"
        >
          {auditOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          Audit Log
          <span className="text-[11px] text-slate font-normal ml-1">(click to {auditOpen ? 'collapse' : 'expand'})</span>
        </button>

        {auditOpen && (
          <div className="border border-[var(--color-border)] rounded-xl overflow-hidden">
            {auditLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : auditLog.length === 0 ? (
              <p className="text-center py-8 text-slate text-small">No changes recorded yet.</p>
            ) : (
              <div className="divide-y divide-[var(--color-border)] max-h-[400px] overflow-y-auto">
                {auditLog.map(entry => (
                  <div key={entry.id} className="px-4 py-3 text-[13px]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="font-medium text-midnight">{entry.changedByName}</span>
                        <span className="text-slate"> changed </span>
                        <span className="font-medium text-midnight">{entry.field}</span>
                        <span className="text-slate"> on </span>
                        <span className="font-medium text-midnight">
                          {entry.entityType === 'state_rule' ? entry.entityId : 'Interstate Rules'}
                        </span>
                      </div>
                      <span className="text-[11px] text-slate shrink-0">{formatTimestamp(entry.changedAt)}</span>
                    </div>
                    <div className="mt-1 text-[12px]">
                      <span className="text-red-600 line-through">{entry.oldValue || '(empty)'}</span>
                      <span className="text-slate mx-1">→</span>
                      <span className="text-emerald-700">{entry.newValue || '(empty)'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Edit Modal ──────────────────────────────────────────────────── */}
      {editingRule && (
        <EditStateModal
          rule={editingRule}
          onSave={updates => handleSaveState(editingRule.stateCode, updates)}
          onClose={() => setEditingRule(null)}
        />
      )}
    </div>
  );
}
